'use client'

import type * as React from 'react'
import { motion } from 'motion/react'

import { cn } from '@repo/ui/lib/utils'

type ShineVisibilityType = 'always' | 'hover'

type ShineDirectionType = 'top' | 'bottom' | 'left' | 'right'

type SpinGlowRotateType = 'clockwise' | 'anticlockwise'

interface IShinyButtonProps extends React.ComponentProps<'button'> {
  containerClassName?: string
  shine?: ShineVisibilityType
  shineDirection?: ShineDirectionType
  shineDelay?: number
  shineDuration?: number
  spinGlow?: ShineVisibilityType
  spinGlowDirection?: ShineDirectionType
  spinGlowRotate?: SpinGlowRotateType
  spinGlowSpeed?: number
}

function getGradientAngleForSpinGlowDirection(
  direction: ShineDirectionType
): number {
  switch (direction) {
    case 'right':
      return 45
    case 'bottom':
      return 135
    case 'left':
      return 225
    case 'top':
      return 315
    default:
      return 135
  }
}

function getShineAnimationInitialPosition(direction: ShineDirectionType): {
  x: string
  y: string
} {
  switch (direction) {
    case 'right':
      return { x: '-100%', y: '0%' }
    case 'left':
      return { x: '200%', y: '0%' }
    case 'top':
      return { x: '0%', y: '200%' }
    case 'bottom':
      return { x: '0%', y: '-100%' }
    default:
      return { x: '-100%', y: '0%' }
  }
}

function getShineAnimationEndPosition(direction: ShineDirectionType): {
  x: string
  y: string
} {
  switch (direction) {
    case 'right':
      return { x: '200%', y: '0%' }
    case 'left':
      return { x: '-100%', y: '0%' }
    case 'top':
      return { x: '0%', y: '-100%' }
    case 'bottom':
      return { x: '0%', y: '200%' }
    default:
      return { x: '200%', y: '0%' }
  }
}

function ShinyButton({
  className,
  containerClassName,
  children,
  shine = 'hover',
  shineDirection = 'right',
  shineDelay = 2,
  shineDuration = 1.5,
  spinGlow,
  spinGlowDirection = 'bottom',
  spinGlowRotate,
  spinGlowSpeed = 4,
  disabled,
  ...props
}: IShinyButtonProps): React.ReactNode {
  const gradientAngle = getGradientAngleForSpinGlowDirection(spinGlowDirection)
  const shineInitial = getShineAnimationInitialPosition(shineDirection)
  const shineAnimate = getShineAnimationEndPosition(shineDirection)

  const rotationAnimation =
    spinGlowRotate && !disabled
      ? {
          rotate: spinGlowRotate === 'clockwise' ? 360 : -360,
        }
      : {}

  const shouldAnimateShine = shine && !disabled

  return (
    <div
      className={cn(
        'group relative flex h-fit w-fit items-center justify-center overflow-hidden rounded-full bg-neutral-300 p-[1.5px] shadow-lg dark:bg-neutral-600',
        disabled && 'opacity-50',
        containerClassName
      )}
    >
      {spinGlow && !disabled && (
        <motion.div
          className={cn(
            'absolute inset-[-1000%] transition-opacity duration-300',
            spinGlow === 'always'
              ? 'opacity-100'
              : 'opacity-0 group-hover:opacity-100'
          )}
          style={{
            background: `conic-gradient(from ${gradientAngle}deg, transparent 0, #FFFFFF 45deg, transparent 90deg)`,
          }}
          animate={rotationAnimation}
          transition={{
            duration: spinGlowSpeed,
            ease: 'linear',
            repeat: Infinity,
          }}
        />
      )}

      <button
        className={cn(
          'relative z-10 inline-flex h-full w-full cursor-pointer items-center justify-center overflow-hidden rounded-full bg-slate-200 px-5 py-3 font-medium text-neutral-600 backdrop-blur-3xl transition-all duration-200 dark:bg-slate-900 dark:text-white',
          disabled && 'cursor-not-allowed',
          className
        )}
        disabled={disabled}
        {...props}
      >
        {shouldAnimateShine && (
          <motion.div
            className={cn(
              'absolute inset-0 z-20 bg-gradient-to-r from-transparent via-white/40 to-transparent transition-opacity duration-300 dark:via-white/20',
              shineDirection === 'top' || shineDirection === 'bottom'
                ? '-skew-y-12 bg-gradient-to-b'
                : '-skew-x-12 bg-gradient-to-r',
              shine === 'always'
                ? 'opacity-100'
                : 'opacity-0 group-hover:opacity-100'
            )}
            initial={shineInitial}
            animate={shineAnimate}
            transition={{
              duration: shineDuration,
              ease: 'easeInOut',
              repeat: Infinity,
              repeatDelay: shineDelay,
            }}
          />
        )}
        {children}
      </button>
    </div>
  )
}

export { ShinyButton, type IShinyButtonProps }
